// speech_utils.js
/**
 * @fileoverview Functions for accessing speech interface
 */

import { WebServices } from 'BMapsSrc/WebServices';
import { CookieManager } from '../CookieManager';

const SKIPGREETING_COOKIE_NAME = 'skipVoiceGreeting';

/**
 * Listen to the microphone, send to whisper, and call provided handleTranscription callback.
 * `timeout` is the number of milliseconds of silence it expects before transcribing the recording.
 * If `autoTranscribe` is true, it will automatically start listening again.
 * This is very specific to the needs of SpeechToText.jsx and ChatBot.jsx.
 * @param {{
 *  handleTranscription: () => any,
 *  timeout: number,
 *  autoTranscribe: boolean,
 * }}
 * @returns {Promise<MediaRecorder>}
*/
export async function captureAndTranscribe({
    handleTranscription, timeout=1000, autoTranscribe=false,
}) {
    const transcriptionOptions = { autoTranscribe };
    const transcriber = await initTranscriber(handleTranscription, transcriptionOptions);
    const listener = new MediaRecorder(await navigator.mediaDevices.getUserMedia({ audio: true }));
    startAudioCapture(transcriber, listener, autoTranscribe, timeout);
    return (listener);
}

/**
 * Send text to the command API to convert it to BMaps commands, if possible.
 * @param {string} words
 * @returns {Promise<object|null>}
 */
export async function wordsToCommands(words, context) {
    if (!words || words.length === 0) return null;
    const commUrl = WebServices.COMMAND_URL;
    const commResponse = await WebServices.startWsRequestJson(commUrl, { prompt: words, context });
    const commResponseData = JSON.parse(commResponse);
    const commandObj = JSON.parse(commResponseData.response);
    return commandObj;
}

async function initTranscriber(handleTranscription, transcriptionOptions={}, onerror) {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaOptions = [
        'audio/webm;codecs=opus', // Common in Chrome and Firefox
        'audio/mp4', // Fallback for older Safari,
        'audio/webm', // Very basic fallback, likely lower quality but widely supported
    ];
    const mimeType = mediaOptions.find((type) => MediaRecorder.isTypeSupported(type));

    if (!mimeType) {
        throw new Error('No supported audio format found for MediaRecorder.');
    }
    const transcriber = new MediaRecorder(stream, { mimeType });
    transcriber.onerror = onerror || ((error) => { console.error('MediaRecorder error: ', error); });
    transcriber.ondataavailable = async (evt) => handleTranscription(
        await sendWhisper(evt.data, transcriptionOptions)
    );
    return transcriber;
}

async function sendWhisper(audioBlob, transcriptionOptions={}) {
    const mimeType = audioBlob.type;
    const baseType = mimeType.split(';')[0]; // codec params get stuck on end... we want the correct file extension, so we strip it. i.e. audio/webm;codecs=opus -> audio/webm
    const extension = baseType.split('/')[1];
    const file = new File([audioBlob], `recording.${extension}`, { type: baseType });
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('transcription_options', JSON.stringify(transcriptionOptions));
    try {
        const response = await fetch(WebServices.SPEECH_URL, {
            method: 'POST',
            body: formData,
        });
        const responseData = await response.json();
        if (!responseData.response) {
            if (responseData.error) {
                console.warn(`Speech to text recognition error: ${responseData.error}`);
            }
            return null;
        }
        console.log(`Transcription: ${responseData.response}`);
        const words = responseData.response.replace('.', '');
        return words;
    } catch (sendError) {
        console.error('Error:', sendError);
        return null;
    }
}

function startAudioCapture(transcriber, listener, autoTranscribe=false, timeout=1000) {
    listener.start();

    const analyzer = createAudioAnalyzer(listener.stream);
    const bufferLength = analyzer.frequencyBinCount;
    const domainData = new Uint8Array(bufferLength);

    // detectSoundStep is a recursive loop called with window.requestanimationframe.
    // It calls itself for as long as the listener is recording.
    // Intention:
    // - Listen until the user is done speaking. ("done" speaking means `timeout` ms of silence)
    // - Stop the recording, causing speech data to be emitted for transcription
    // - Start recording again, if ongoing transcription is requested
    let timer = null;
    const detectSoundStep = () => {
        let soundDetected = false;

        // check if user is speaking... if so we will reset the timeout.
        analyzer.getByteFrequencyData(domainData);
        for (let i = 0; i < bufferLength; i++) {
            const value = domainData[i];
            if (value > 0) {
                soundDetected = true;
                break;
            }
        }

        if (soundDetected) {
            if (transcriber.state !== 'recording') {
                transcriber.start();
            }
            clearTimeout(timer);
            timer = setTimeout(() => {
                if (transcriber.state === 'recording') {
                    // mediaRecorder sends it's information to whisper
                    transcriber.stop();
                    if (!autoTranscribe) {
                        listener.stop();
                    }
                }
            }, timeout);
        }

        if (listener.state === 'recording') {
            window.requestAnimationFrame(detectSoundStep);
        }
    };
    // Start the Media recorder loop.
    // To end loop, call listener.stop(),
    // and requestAnimationFrome(detectSoundStep) won't trigger.
    window.requestAnimationFrame(detectSoundStep);
}

function createAudioAnalyzer(stream) {
    const audioContext = new AudioContext();
    const audioStreamSource = audioContext.createMediaStreamSource(stream);
    const analyzer = audioContext.createAnalyser();
    analyzer.minDecibels = -45;
    audioStreamSource.connect(analyzer);
    return analyzer;
}

export function setSkipGreeting(boolean) {
    if (boolean) {
        CookieManager.set(SKIPGREETING_COOKIE_NAME, 'set', -1);
    } else {
        CookieManager.clear(SKIPGREETING_COOKIE_NAME);
    }
}

export function isSkippingGreeting() {
    if (CookieManager.exists(SKIPGREETING_COOKIE_NAME)) {
        return CookieManager.get(SKIPGREETING_COOKIE_NAME) != null;
    } else {
        return false;
    }
}
